import { useState, useEffect, useRef } from "react";
import API from "../../lib/api";

const MENTION_RE = /@([\w.-]+)/g;

export function renderWithMentions(text = "") {
  const parts = [];
  let lastIdx = 0;
  let m;
  MENTION_RE.lastIndex = 0;
  while ((m = MENTION_RE.exec(text)) !== null) {
    if (m.index > lastIdx) parts.push(text.slice(lastIdx, m.index));
    parts.push(
      <span key={`mention-${m.index}`} className="inline-flex items-center rounded px-1 py-0.5 text-[11px] font-semibold bg-amber-200/60 text-amber-900 dark:bg-amber-500/20 dark:text-amber-300">
        @{m[1]}
      </span>
    );
    lastIdx = m.index + m[0].length;
  }
  if (lastIdx < text.length) parts.push(text.slice(lastIdx));
  return parts;
}

/* @mention autocomplete for a textarea: detects "@query" at the caret, filters teammates, inserts the handle. */
export function useMentions(text, setText, enabled = true) {
  const [mentionable, setMentionable] = useState([]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [start, setStart] = useState(-1);
  const [idx, setIdx] = useState(0);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!enabled) return;
    API.get("/private-notes/mentionable-users").then(({ data }) => setMentionable(data.users || [])).catch(() => {});
  }, [enabled]);

  const onChangeText = (e) => {
    const val = e.target.value;
    setText(val);
    if (!enabled) return;
    const caret = e.target.selectionStart ?? val.length;
    const match = val.slice(0, caret).match(/(^|\s)@([\w.-]*)$/);
    if (match) {
      setOpen(true);
      setQuery(match[2].toLowerCase());
      setStart(caret - match[2].length - 1);
      setIdx(0);
    } else {
      setOpen(false);
    }
  };

  const filtered = mentionable
    .filter((u) => !query || (u.name || "").toLowerCase().includes(query) || (u.handle || "").toLowerCase().includes(query) || (u.email || "").toLowerCase().split("@")[0].includes(query))
    .slice(0, 6);

  const insertMention = (u) => {
    if (start < 0) return;
    const handle = u.handle || (u.name || "").replace(/\s+/g, ".");
    const before = text.slice(0, start);
    const after = text.slice(textareaRef.current?.selectionStart ?? text.length);
    setText(`${before}@${handle} ${after}`);
    setOpen(false);
    setTimeout(() => {
      textareaRef.current?.focus();
      const pos = (before + `@${handle} `).length;
      textareaRef.current?.setSelectionRange(pos, pos);
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (!open || filtered.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => (i + 1) % filtered.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => (i - 1 + filtered.length) % filtered.length); }
    else if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); insertMention(filtered[idx]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return { textareaRef, onChangeText, handleKeyDown, open: open && filtered.length > 0, filtered, idx, setIdx, insertMention };
}
